import { useState, useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  MdDashboard,
  MdChecklist,
  MdTaskAlt,
  MdEventNote,
  MdSchool,
  MdFolderShared,
  MdBusiness,
  MdPeople,
  MdAssignment,
  MdTrendingUp,
  MdClose,
  MdKeyboardArrowDown,
  MdNoteAlt,
  MdPendingActions,
  MdBackup,
  MdQuiz,
  MdAdd,
  MdAssessment,
} from "react-icons/md";
import api from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import "./Sidebar.css";

const API_URL =
  process.env.NODE_ENV === "production"
    ? process.env.REACT_APP_API_URL || window.location.origin
    : "http://localhost:5000";

const getInitials = (name) =>
  name
    ?.split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2) || "?";

const Sidebar = ({ isOpen = false, onClose }) => {
  const { user, isAdmin, isSuperAdmin } = useAuth();
  const location = useLocation();
  const role = user?.role === "user" ? "staff" : user?.role;
  const canViewReports =
    role === "super_admin" || role === "hr" || role === "accounts";
  const roleLabel =
    role === "super_admin"
      ? "Super Admin"
      : role === "admin"
        ? "Admin"
        : role === "accounts"
          ? "Accounts"
          : role === "hr"
            ? "HR"
            : "Staff";
  const colors = [
    "#6366f1",
    "#f59e0b",
    "#10b981",
    "#ef4444",
    "#3b82f6",
    "#8b5cf6",
  ];
  const color = colors[user?.name?.charCodeAt(0) % colors.length] || "#6366f1";

  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [notepadOpen, setNotepadOpen] = useState(false);
  const [assessmentReportsOpen, setAssessmentReportsOpen] = useState(
    location.pathname.startsWith("/assessments/reports"),
  );
  const [pendingDoneCount, setPendingDoneCount] = useState(0);

  useEffect(() => {
    if (location.pathname.startsWith("/assessments/reports")) {
      setAssessmentReportsOpen(true);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (!user) {
      setPendingDoneCount(0);
      return;
    }
    const refreshCount = () => {
      api
        .get("/done-requests/pending/count")
        .then(({ data }) => setPendingDoneCount(data.count || 0))
        .catch(() => setPendingDoneCount(0));
    };
    refreshCount();
    window.addEventListener("pending-done-count-changed", refreshCount);
    return () =>
      window.removeEventListener("pending-done-count-changed", refreshCount);
  }, [user?._id, user?.id]);

  return (
    <aside
      className={`sidebar ${isOpen ? "sidebar-open" : ""}`}
      aria-hidden={!isOpen}
    >
      <div className="sidebar-brand sidebar-brand-user">
        <button
          type="button"
          className="sidebar-close-btn"
          onClick={() => onClose?.()}
          aria-label="Close menu"
          title="Close"
        >
          <MdClose size={22} />
        </button>
        <div
          className="sidebar-avatar"
          style={{ background: user?.avatar ? "transparent" : color }}
        >
          {user?.avatar ? (
            <img
              src={
                user.avatar.startsWith("http")
                  ? user.avatar
                  : `${API_URL}${user.avatar}`
              }
              alt={user?.name}
              onError={(e) => {
                e.target.style.display = "none";
                e.target.parentNode.textContent = getInitials(user?.name);
              }}
            />
          ) : (
            getInitials(user?.name)
          )}
        </div>
        <div className="sidebar-user-details">
          <span className="sidebar-user-name">{user?.name || "User"}</span>
          <span className="sidebar-admin-label">{roleLabel} Panel</span>
        </div>
      </div>

      <nav className="sidebar-nav">
        <div className="nav-label">Menu</div>

        <NavLink
          to="/dashboard"
          className={({ isActive }) =>
            `nav-item nav-dashboard ${isActive ? "active" : ""}`
          }
          onClick={() => onClose?.()}
        >
          <span className="nav-icon">
            <MdDashboard />
          </span>
          <span>Dashboard</span>
        </NavLink>

        <NavLink
          to="/tasks"
          className={({ isActive }) =>
            `nav-item nav-tasks ${isActive ? "active" : ""}`
          }
          onClick={() => onClose?.()}
        >
          <span className="nav-icon">
            <MdChecklist />
          </span>
          <span>My Tasks</span>
        </NavLink>

        <NavLink
          to="/completed-tasks"
          className={({ isActive }) =>
            `nav-item nav-tasks ${isActive ? "active" : ""}`
          }
          onClick={() => onClose?.()}
        >
          <span className="nav-icon">
            <MdTaskAlt />
          </span>
          <span>Completed Tasks</span>
        </NavLink>

        <NavLink
          to="/pending-done-requests"
          className={({ isActive }) =>
            `nav-item nav-tasks ${isActive ? "active" : ""}`
          }
          onClick={() => onClose?.()}
        >
          <span className="nav-icon">
            <MdPendingActions />
          </span>
          <span>Pending Done Requests</span>
          {pendingDoneCount > 0 && (
            <span className="nav-badge" aria-label={`${pendingDoneCount} pending`}>
              {pendingDoneCount}
            </span>
          )}
        </NavLink>

        {/* Collapsible Scheduled Tasks group (accordion style) */}
        <div className="nav-group">
          <button
            type="button"
            className={`nav-item nav-tasks nav-group-header ${
              scheduleOpen ? "open" : ""
            }`}
            onClick={() => setScheduleOpen((v) => !v)}
            style={{
              width: "100%",
              textAlign: "left",
              justifyContent: "space-between",
              border: "none",
            }}
          >
            <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span className="nav-icon">
                <MdEventNote />
              </span>
              <span>Schedule a task</span>
            </span>
            <MdKeyboardArrowDown
              size={18}
              style={{
                transform: scheduleOpen ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.15s",
                color: "#9ca3af",
              }}
            />
          </button>
          {scheduleOpen && (
            <div className="nav-sublist">
              <NavLink
                to="/scheduled-tasks/new"
                className={({ isActive }) =>
                  `nav-subitem ${isActive ? "active" : ""}`
                }
                onClick={() => onClose?.()}
              >
                Create new schedule
              </NavLink>
              <NavLink
                to="/scheduled-tasks/all"
                className={({ isActive }) =>
                  `nav-subitem ${isActive ? "active" : ""}`
                }
                onClick={() => onClose?.()}
              >
                View all scheduled tasks
              </NavLink>
            </div>
          )}
        </div>

        <NavLink
          to="/enrollments"
          className={({ isActive }) =>
            `nav-item nav-enrollments ${isActive ? "active" : ""}`
          }
          onClick={() => onClose?.()}
        >
          <span className="nav-icon">
            <MdSchool />
          </span>
          <span>Upload Enrollment Sheets</span>
        </NavLink>

        {/* Collapsible Notepad group */}
        <div className="nav-group">
          <button
            type="button"
            className={`nav-item nav-notepad nav-group-header ${notepadOpen ? "open" : ""}`}
            onClick={() => setNotepadOpen((v) => !v)}
            style={{ width: "100%", textAlign: "left", justifyContent: "space-between", border: "none" }}
          >
            <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span className="nav-icon"><MdNoteAlt /></span>
              <span>Notepad</span>
            </span>
            <MdKeyboardArrowDown
              size={18}
              style={{ transform: notepadOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.15s", color: "#9ca3af" }}
            />
          </button>
          {notepadOpen && (
            <div className="nav-sublist">
              <NavLink
                to="/notepad"
                className={({ isActive }) => `nav-subitem ${isActive ? "active" : ""}`}
                onClick={() => onClose?.()}
              >
                My Notepad
              </NavLink>
              {isSuperAdmin && (
                <NavLink
                  to="/admin/notepad"
                  className={({ isActive }) => `nav-subitem ${isActive ? "active" : ""}`}
                  onClick={() => onClose?.()}
                >
                  All Notepads
                </NavLink>
              )}
            </div>
          )}
        </div>

        <div className="nav-label" style={{ marginTop: 20 }}>
          Staff Assessments
        </div>

        <NavLink
          to="/assessments/new"
          className={({ isActive }) =>
            `nav-item nav-assessments ${isActive ? "active" : ""}`
          }
          onClick={() => onClose?.()}
        >
          <span className="nav-icon">
            <MdAdd />
          </span>
          <span>Create Assessment</span>
        </NavLink>

        <NavLink
          to="/assessments"
          end
          className={({ isActive }) =>
            `nav-item nav-assessments ${isActive ? "active" : ""}`
          }
          onClick={() => onClose?.()}
        >
          <span className="nav-icon">
            <MdQuiz />
          </span>
          <span>Assigned Assessments</span>
        </NavLink>

        <NavLink
          to="/assessments/created"
          className={({ isActive }) =>
            `nav-item nav-assessments ${isActive ? "active" : ""}`
          }
          onClick={() => onClose?.()}
        >
          <span className="nav-icon">
            <MdAssignment />
          </span>
          <span>
            {isSuperAdmin ? "All Assessments" : "Assessments I Created"}
          </span>
        </NavLink>

        {/* Collapsible Assessment Reporting */}
        <div className="nav-group">
          <button
            type="button"
            className={`nav-item nav-assessments nav-group-header ${
              assessmentReportsOpen ? "open" : ""
            }`}
            onClick={() => setAssessmentReportsOpen((v) => !v)}
            style={{
              width: "100%",
              textAlign: "left",
              justifyContent: "space-between",
              border: "none",
            }}
          >
            <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span className="nav-icon">
                <MdAssessment />
              </span>
              <span>Assessment Reports</span>
            </span>
            <MdKeyboardArrowDown
              size={18}
              style={{
                transform: assessmentReportsOpen
                  ? "rotate(180deg)"
                  : "rotate(0deg)",
                transition: "transform 0.15s",
                color: "#9ca3af",
              }}
            />
          </button>
          {assessmentReportsOpen && (
            <div className="nav-sublist">
              <NavLink
                to="/assessments/reports/completion"
                className={({ isActive }) =>
                  `nav-subitem ${isActive ? "active" : ""}`
                }
                onClick={() => onClose?.()}
              >
                Assessment Completion
              </NavLink>
            </div>
          )}
        </div>

        {isAdmin && (
          <>
            <div className="nav-label" style={{ marginTop: 20 }}>
              Admin
            </div>

            <NavLink
              to="/admin/tasks"
              className={({ isActive }) =>
                `nav-item nav-admin-tasks ${isActive ? "active" : ""}`
              }
              onClick={() => onClose?.()}
            >
              <span className="nav-icon">
                <MdAssignment />
              </span>
              <span>Manage Staff Tasks</span>
            </NavLink>

            <NavLink
              to="/admin/enrollments"
              className={({ isActive }) =>
                `nav-item nav-admin-enrollments ${isActive ? "active" : ""}`
              }
              onClick={() => onClose?.()}
            >
              <span className="nav-icon">
                <MdFolderShared />
              </span>
              <span>Staff Enrollments</span>
            </NavLink>

            {/* Organization Management: only visible to Super Admin, hidden from Admin/Accounts/HR */}
            {isSuperAdmin && (
              <>
                <div className="nav-label" style={{ marginTop: 20 }}>
                  Organization Management
                </div>

                <NavLink
                  to="/admin/organizations"
                  className={({ isActive }) =>
                    `nav-item nav-admin-orgs ${isActive ? "active" : ""}`
                  }
                  onClick={() => onClose?.()}
                >
                  <span className="nav-icon">
                    <MdBusiness />
                  </span>
                  <span>Organizations</span>
                </NavLink>
                <NavLink
                  to="/admin/staff"
                  className={({ isActive }) =>
                    `nav-item nav-admin-staff ${isActive ? "active" : ""}`
                  }
                  onClick={() => onClose?.()}
                >
                  <span className="nav-icon">
                    <MdPeople />
                  </span>
                  <span>Manage Staff</span>
                </NavLink>
                <NavLink
                  to="/admin/database-backup"
                  className={({ isActive }) =>
                    `nav-item nav-admin-backup ${isActive ? "active" : ""}`
                  }
                  onClick={() => onClose?.()}
                >
                  <span className="nav-icon">
                    <MdBackup />
                  </span>
                  <span>Database Backup</span>
                </NavLink>
              </>
            )}

            {/* Reports: only Super Admin, HR, Accounts */}
            {canViewReports && (
              <>
                <div className="nav-label" style={{ marginTop: 20 }}>
                  Reports
                </div>
                <NavLink
                  to="/admin/reports/performance"
                  className={({ isActive }) =>
                    `nav-item nav-admin-reports ${isActive ? "active" : ""}`
                  }
                  onClick={() => onClose?.()}
                >
                  <span className="nav-icon">
                    <MdTrendingUp />
                  </span>
                  <span>Staff Performance</span>
                </NavLink>
                <NavLink
                  to="/admin/reports/workload"
                  className={({ isActive }) =>
                    `nav-item nav-admin-reports ${isActive ? "active" : ""}`
                  }
                  onClick={() => onClose?.()}
                >
                  <span className="nav-icon">
                    <MdTrendingUp />
                  </span>
                  <span>Staff Workload</span>
                </NavLink>
                <NavLink
                  to="/admin/reports/priority"
                  className={({ isActive }) =>
                    `nav-item nav-admin-reports ${isActive ? "active" : ""}`
                  }
                  onClick={() => onClose?.()}
                >
                  <span className="nav-icon">
                    <MdTrendingUp />
                  </span>
                  <span>Task Priority</span>
                </NavLink>
                <NavLink
                  to="/admin/reports/activity"
                  className={({ isActive }) =>
                    `nav-item nav-admin-reports ${isActive ? "active" : ""}`
                  }
                  onClick={() => onClose?.()}
                >
                  <span className="nav-icon">
                    <MdTrendingUp />
                  </span>
                  <span>Task Activity Log</span>
                </NavLink>
                <NavLink
                  to="/admin/reports/overdue"
                  className={({ isActive }) =>
                    `nav-item nav-admin-reports ${isActive ? "active" : ""}`
                  }
                  onClick={() => onClose?.()}
                >
                  <span className="nav-icon">
                    <MdTrendingUp />
                  </span>
                  <span>Overdue Tasks</span>
                </NavLink>
                <NavLink
                  to="/admin/reports/not-done"
                  className={({ isActive }) =>
                    `nav-item nav-admin-reports ${isActive ? "active" : ""}`
                  }
                  onClick={() => onClose?.()}
                >
                  <span className="nav-icon">
                    <MdTrendingUp />
                  </span>
                  <span>Not Done Tasks</span>
                </NavLink>
              </>
            )}
          </>
        )}
      </nav>
    </aside>
  );
};

export default Sidebar;
